import { FormControl, InputLabel, MenuItem } from "@mui/material"
import { FormFieldDto, FormFieldPrevalueDto } from "api"
import { useEffect, useRef, useState } from "react"
import { Controller, FieldValues } from "react-hook-form"
import { colors } from "ui"
import { useExternalApiFormStore } from "../FormContext"
import { useRelatedFields } from "../RelatedFieldsInFromContext"
import { StyledHelperText } from "../StyledComponents"
import { StyledSelect } from "./styles"

interface Props {
  field: FormFieldDto
  control: any
  error: any
  isSubmitted: boolean
  watchedValues?: FieldValues
  fieldName?: string // Add optional fieldName prop
}

const SelectField = ({
  field,
  control,
  error,
  isSubmitted,
  watchedValues,
  fieldName,
}: Props) => {
  const { data } = useExternalApiFormStore() || { data: null }
  const { relatedFields } = useRelatedFields() || { relatedFields: null }
  const prevWatchedValues = useRef<FieldValues>()

  const [items, setItems] = useState(field?.preValues || [])
  const allItemsRef = useRef<FormFieldPrevalueDto[]>([])
  const actualFieldName = fieldName || field.id // Use fieldName if provided, fallback to field.id

  useEffect(() => {
    if (!data) {
      return
    }
    const itemsFromStore = data[field.caption!]
    if (!itemsFromStore) {
      return
    }
    allItemsRef.current = itemsFromStore
    setItems(itemsFromStore)
  }, [data])

  useEffect(() => {
    if (!relatedFields || !field?.caption) {
      return
    }

    // get relate field key value from context
    const fieldRelatedId = relatedFields[field.caption]
    if (!fieldRelatedId || !watchedValues) return
    const valueOfRelatedField = watchedValues[fieldRelatedId]

    // filter by it
    const currentItems =
      allItemsRef.current.filter(
        (item) => item?.parentCode === valueOfRelatedField,
      ) || []

    setItems(currentItems)

    return () => {
      prevWatchedValues.current = watchedValues
    }
  }, [watchedValues])

  return (
    <FormControl fullWidth variant="filled" error={!!error} size="medium">
      <InputLabel htmlFor={actualFieldName}>{field.helpText}</InputLabel>
      <Controller
        name={actualFieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <StyledSelect
            id={actualFieldName}
            value={value || ""}
            onChange={onChange}
            inputProps={{
              "aria-invalid": !!error,
              "aria-describedby": `${actualFieldName}-helper-text`,
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {},
            }}
          >
            {items.map((preValue, index) => (
              <MenuItem
                key={`${index}-${preValue.value}`}
                value={preValue.value}
                sx={{ color: colors.black }}
              >
                {preValue.caption || preValue.value}
              </MenuItem>
            ))}
          </StyledSelect>
        )}
      />
      <StyledHelperText id={`${actualFieldName}-helper-text`} status={!error}>
        {isSubmitted && error && (
          <>
            <span>{error?.message as string}</span>
          </>
        )}
      </StyledHelperText>
    </FormControl>
  )
}

export default SelectField
